import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { registry } from "@/api/client";
import { Select, SearchInput } from "@/components/inputs";

import { Icon } from "@/components/icons";
/** Pick things out of one company's stock, by searching rather than typing.
 *
 *  WHY IT EXISTS. These forms asked you to TYPE an identifier — a fifteen-digit
 *  IMEI, a VIN, a serial — from memory or from another window. That is the
 *  least forgiving input a form can ask for: no feedback until submit, and a
 *  typo used to move a different company's equipment. Once the form knows
 *  WHICH company it is acting on, it can show what that company actually has.
 *
 *  TWO SCOPES, because ownership and custody are different questions (D-82):
 *    owner     — what this company OWNS. Transfer's question: only the owner
 *                may sell.
 *    custodian — what this company HOLDS. Ship's question: a repairer sends
 *                back a vehicle they hold and do not own, and searching by
 *                owner would show them nothing to send.
 *
 *  A PICK CARRIES BOTH IDS, because the endpoints disagree and that is not the
 *  caller's problem to remember: /transfer takes human identifiers (VIN,
 *  serial, IMEI) and resolves them itself, while /ship and /receive take
 *  `asset_ids` and `part_ids`, and a part's id is its UUID, not its serial.
 *  Sending a serial where a UUID belongs fails as "unknown part", which reads
 *  like the part is missing rather than like the wrong field was sent.
 *
 *  NOT asset_data's DevicePicker: that one resolves every match to the
 *  Connection ID whose live stream to open, and is built for customer roles
 *  that cannot reach the internal catalog endpoints. Sharing them would mean
 *  one component with two return types and two scoping rules.
 *
 *  Searches are capped at 25 and need 2+ characters, so this stays O(page)
 *  however much stock a company holds (ARCHITECTURE.md, UI performance laws).
 */

export type PickKind = "asset" | "part" | "device";

export interface StockPick {
  kind: PickKind;
  /** What a person recognises, and what /transfer accepts: VIN, serial, IMEI. */
  id: string;
  /** What /ship and /receive accept. Same as `id` for assets; a UUID for parts. */
  apiId: string;
  detail: string;
}

const LABEL: Record<PickKind, string> = {
  asset: "Assets - VIN / BIN",
  part: "Subcomponents - serial",
  device: "Devices - IMEI / board id",
};

export function StockPicker({ company, scopeBy = "owner", kinds = ["asset", "part", "device"],
                              selected, onChange }: {
  /** The company whose stock is searched. Empty = nothing to search yet. */
  company: string;
  /** Search what they OWN (transfer) or what they HOLD (ship). */
  scopeBy?: "owner" | "custodian";
  /** Which kinds this form can act on. /ship takes no connections, so the
   *  Ship form passes ["asset", "part"] rather than offering a dead option. */
  kinds?: PickKind[];
  selected: StockPick[];
  onChange: (next: StockPick[]) => void;
}) {
  const [by, setBy] = useState<PickKind>(kinds[0]);
  const [search, setSearch] = useState("");

  // Two characters before we ask: a one-letter search matches most of a fleet
  // and the answer is useless anyway.
  const ready = !!company && search.trim().length >= 2;
  const scope = scopeBy === "custodian" ? { custodian: company } : { company };
  const q = { ...scope, search: search.trim(), limit: "25" };

  const assets = useQuery({
    queryKey: ["reg", "pick", "asset", scopeBy, company, search],
    queryFn: () => registry.assets(q), enabled: ready && by === "asset",
  });
  const parts = useQuery({
    queryKey: ["reg", "pick", "part", scopeBy, company, search],
    queryFn: () => registry.parts(q), enabled: ready && by === "part",
  });
  const devices = useQuery({
    queryKey: ["reg", "pick", "device", scopeBy, company, search],
    queryFn: () => registry.devices({ company, search: search.trim(), limit: "25" }),
    enabled: ready && by === "device",
  });

  const busy = assets.isFetching || parts.isFetching || devices.isFetching;

  const hits: StockPick[] =
    by === "asset"
      ? (assets.data?.assets ?? []).map((a) => ({
          kind: "asset" as const, id: a.asset_id, apiId: a.asset_id,
          detail: [a.label, a.model, a.state].filter(Boolean).join(" · "),
        }))
      : by === "part"
        ? (parts.data?.parts ?? []).map((p) => ({
            kind: "part" as const, id: p.serial_no, apiId: p.part_id,
            detail: [p.kind_label, p.model,
                     p.installed_in ? `installed in ${p.installed_in}` : p.state]
              .filter(Boolean).join(" · "),
          }))
        : (devices.data?.devices ?? []).map((d) => ({
            kind: "device" as const, id: d.imei, apiId: d.imei,
            detail: [d.board_id, d.state].filter(Boolean).join(" · "),
          }));

  const has = (p: StockPick) => selected.some((s) => s.apiId === p.apiId);
  const add = (p: StockPick) => { if (!has(p)) onChange([...selected, p]); setSearch(""); };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        {kinds.length > 1 && (
          <Select value={by} onChange={(e) => { setBy(e.target.value as PickKind); setSearch(""); }}
                  style={{ minWidth: 200 }} disabled={!company}>
            {kinds.map((k) => <option key={k} value={k}>{LABEL[k]}</option>)}
          </Select>
        )}
        <SearchInput mono style={{ flex: "1 1 240px", minWidth: 0 }} value={search} disabled={!company}
                     placeholder={company ? "type 2+ characters…" : "choose a company first"}
                     onChange={(e) => setSearch(e.target.value)} />
      </div>

      {ready && (
        <div style={{ marginTop: 8, border: "1px solid var(--line2)", borderRadius: 10,
                      maxHeight: 210, overflowY: "auto" }}>
          {busy && <div className="cap" style={{ padding: "8px 10px" }}>searching…</div>}
          {!busy && hits.length === 0 && (
            <div className="cap" style={{ padding: "8px 10px" }}>
              nothing matches “{search.trim()}”
            </div>
          )}
          {hits.map((h) => (
            <button key={h.apiId} type="button" onClick={() => add(h)} disabled={has(h)}
                    style={{ display: "flex", width: "100%", gap: 10, alignItems: "baseline",
                             padding: "7px 10px", background: "none", border: "none",
                             borderBottom: "1px solid var(--line2)", textAlign: "left",
                             cursor: has(h) ? "default" : "pointer",
                             opacity: has(h) ? 0.45 : 1, color: "var(--t-body)" }}>
              <span className="mono" style={{ fontSize: 12.5 }}>{h.id}</span>
              <span className="cap" style={{ fontSize: 11.5 }}>{h.detail}</span>
              {has(h) && <span className="cap" style={{ marginLeft: "auto" }}>added</span>}
            </button>
          ))}
        </div>
      )}

      {selected.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
          {selected.map((p) => (
            <span key={p.apiId} className="chip"
                  style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
              <span className="mono">{p.id}</span>
              <button type="button" title="Remove"
                      onClick={() => onChange(selected.filter((x) => x.apiId !== p.apiId))}
                      style={{ background: "none", border: "none", cursor: "pointer",
                               color: "inherit", padding: 0, lineHeight: 1 }}><Icon name="close" /></button>
            </span>
          ))}
          <button type="button" className="btn ghost" onClick={() => onChange([])}>Clear all</button>
        </div>
      )}
    </div>
  );
}
